/** Admin data layer (05 §5 `/admin/*`). */

import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'

import { api } from './api'
import type {
  AdminProduct,
  AdminProductPage,
  AdminStore,
  CsvImportResult,
  DataQuality,
  LaborRate,
  ManualProductInput,
  ScrapeRun,
  SessionReplay,
  SessionSummary,
} from './admintypes'

export function useStores() {
  return useQuery({
    queryKey: ['admin', 'stores'],
    queryFn: () => api.get<AdminStore[]>('/admin/stores'),
    refetchInterval: 60_000,
  })
}

export function useUpdateStore() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ key, ...body }: { key: string } & Record<string, unknown>) =>
      api.patch<AdminStore>(`/admin/stores/${key}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  })
}

export function useTriggerScrape() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({
      key,
      kind = 'full',
      only = '',
    }: {
      key: string
      kind?: 'full' | 'prices'
      /** Narrow a full crawl to categories whose URL contains this. */
      only?: string
    }) => {
      const params = new URLSearchParams({ kind })
      if (only.trim()) params.set('only', only.trim())
      return api.post(`/admin/stores/${key}/scrape?${params}`)
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['admin', 'runs'] })
      void queryClient.invalidateQueries({ queryKey: ['admin', 'stores'] })
    },
  })
}

export function useScrapeRuns(store?: string) {
  const query = store ? `?store=${encodeURIComponent(store)}` : ''
  return useQuery({
    queryKey: ['admin', 'runs', store ?? 'all'],
    queryFn: () => api.get<ScrapeRun[]>(`/admin/scrape-runs${query}`),
    refetchInterval: 30_000,
  })
}

export interface ProductFilters {
  q?: string
  store?: string
  slot?: string
  whitelisted?: boolean
  needs_slot?: boolean
  stale?: boolean
  offset?: number
  limit?: number
}

function productQuery(filters: ProductFilters): string {
  const params = new URLSearchParams()
  if (filters.q) params.set('q', filters.q)
  if (filters.store) params.set('store', filters.store)
  if (filters.slot) params.set('slot', filters.slot)
  if (filters.whitelisted !== undefined) params.set('whitelisted', String(filters.whitelisted))
  if (filters.needs_slot) params.set('needs_slot', 'true')
  if (filters.stale) params.set('stale', 'true')
  params.set('offset', String(filters.offset ?? 0))
  params.set('limit', String(filters.limit ?? 50))
  return params.toString()
}

export function useProducts(filters: ProductFilters) {
  const query = productQuery(filters)
  return useQuery({
    queryKey: ['admin', 'products', query],
    queryFn: () => api.get<AdminProductPage>(`/admin/products?${query}`),
    placeholderData: (previous) => previous,
  })
}

export function useUpdateProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ id, ...body }: { id: string } & Record<string, unknown>) =>
      api.patch(`/admin/products/${id}`, body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'products'] }),
  })
}

export function useBulkUpdate() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: {
      product_ids: string[]
      whitelisted?: boolean
      preferred?: boolean
      add_slots?: string[]
      remove_slots?: string[]
      note?: string
    }) => api.post<{ updated: number }>('/admin/products:bulk', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  })
}

/** 06 §8: a supplier with no scrapable site, entered by hand. */
export function useCreateProduct() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (body: ManualProductInput) => api.post<AdminProduct>('/admin/products', body),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  })
}

/** The same thing by the hundred. Rows that are wrong are reported and skipped, not fatal. */
export function useImportCsv() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: ({ file, store, whitelist }: { file: File; store: string; whitelist: boolean }) => {
      const form = new FormData()
      form.append('file', file)
      const query = new URLSearchParams({ store_key: store, whitelist: String(whitelist) })
      return api.upload<CsvImportResult>(`/admin/products:import?${query}`, form)
    },
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin'] }),
  })
}

export function useLaborRates() {
  return useQuery({
    queryKey: ['admin', 'labor'],
    queryFn: () => api.get<LaborRate[]>('/admin/labor-rates'),
  })
}

export function useSetLaborRates() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: (rates: { key: string; from_price: number | null; notes?: string }[]) =>
      api.put<LaborRate[]>('/admin/labor-rates', { rates }),
    onSuccess: () => queryClient.invalidateQueries({ queryKey: ['admin', 'labor'] }),
  })
}

/** WS6-12: recent sessions, newest activity first. */
export function useSessions({ guardrailOnly = false }: { guardrailOnly?: boolean } = {}) {
  const query = guardrailOnly ? '?guardrail_only=true' : ''
  return useQuery({
    queryKey: ['admin', 'sessions', guardrailOnly],
    queryFn: () => api.get<SessionSummary[]>(`/admin/sessions${query}`),
    refetchInterval: 60_000,
  })
}

/** One session in full. Only fetched when a row is expanded — the payload is the transcript. */
export function useSessionReplay(sessionId: string | null) {
  return useQuery({
    queryKey: ['admin', 'sessions', sessionId],
    queryFn: () => api.get<SessionReplay>(`/admin/sessions/${sessionId}`),
    enabled: Boolean(sessionId),
  })
}

export function useDataQuality() {
  return useQuery({
    queryKey: ['admin', 'quality'],
    queryFn: () => api.get<DataQuality>('/admin/data-quality'),
    refetchInterval: 120_000,
  })
}
